import { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Download, Save, CheckCircle2, Home, Copy } from "lucide-react";
import { projectsApi, materialsApi } from "@/lib/api";
import {
  frameAreas, framePriced, DEFAULT_FRAME, DEFAULT_AREA,
  fmt, fmtMoney, WOOD_SYSTEM_TYPES, MANUAL_GROUPS,
} from "@/lib/calc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const newWall = () => ({ id: crypto.randomUUID(), name: "", width: "", height: "" });
const newOpening = () => ({ id: crypto.randomUUID(), width: "", height: "", count: 1 });
const UNIT_MAP = { "m²": "m2", "m³": "m3" };

export default function FrameHouseEditor({ id, initialProject }) {
  const nav = useNavigate();
  const [project, setProject] = useState(() => ({
    ...initialProject,
    system_type: initialProject.system_type || "karkas",
    frame: { ...DEFAULT_FRAME, ...(initialProject.frame || {}) },
  }));
  const [materials, setMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  const st = project.system_type;
  const isYigma = st === "yigma";
  const isClt = st === "clt";
  const k = project.frame;
  const materialsById = Object.fromEntries(materials.map((m) => [m.id, m]));
  const areas = frameAreas(k);
  const { rows: comps, manual, grand } = framePriced(k, materialsById, st);
  const overheadPct = parseFloat(k.overhead_pct) || 0;
  const overhead = grand * overheadPct / 100;
  const total = grand + overhead;
  const computedSum = comps.reduce((s, c) => s + c.lineTotal, 0);
  const groupSums = {};
  manual.forEach((m) => { groupSums[m.group] = (groupSums[m.group] || 0) + m.lineTotal; });
  const materialTotal = computedSum + (groupSums["Malzeme"] || 0);
  const otherGroups = [...MANUAL_GROUPS.filter((g) => g !== "Malzeme" && groupSums[g]), ...Object.keys(groupSums).filter((g) => !MANUAL_GROUPS.includes(g) && groupSums[g])];

  useEffect(() => { materialsApi.list().then(setMaterials).catch(() => {}); }, []);

  const persist = useCallback(async (p) => {
    setSaving(true);
    try {
      await projectsApi.update(id, {
        name: p.name, category: "wooden_house", system_type: p.system_type,
        prepared_by: p.prepared_by, project_date: p.project_date,
        price_mode: p.price_mode || "live", line_items: [], frame: p.frame,
        variant_group: p.variant_group || null, group_name: p.group_name || null,
      });
      setSavedAt(Date.now());
    } catch (e) {
      toast.error("Otomatik kayıt başarısız");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = (mutator) => {
    setProject((prev) => {
      const next = mutator({ ...prev });
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };

  const setMeta = (field, value) => update((p) => ({ ...p, [field]: value }));
  const setFrame = (field, value) => update((p) => ({ ...p, frame: { ...p.frame, [field]: value } }));
  const setArea = (key, field, value) => update((p) => ({
    ...p, frame: { ...p.frame, [key]: { ...(p.frame[key] || DEFAULT_AREA), [field]: value } },
  }));

  const changeSystem = async (v) => {
    clearTimeout(saveTimer.current);
    const next = { ...project, system_type: v };
    setProject(next);
    await persist(next);
    window.location.reload();
  };

  // wall + opening list helpers
  const setItem = (field, idx, prop, value) => update((p) => {
    const arr = p.frame[field].slice();
    arr[idx] = { ...arr[idx], [prop]: value };
    return { ...p, frame: { ...p.frame, [field]: arr } };
  });
  const addItem = (field, factory) => update((p) => ({ ...p, frame: { ...p.frame, [field]: [...p.frame[field], factory()] } }));
  const removeItem = (field, idx) => update((p) => {
    const arr = p.frame[field].slice();
    arr.splice(idx, 1);
    return { ...p, frame: { ...p.frame, [field]: arr } };
  });

  const setComp = (compKey, prop, value) => update((p) => {
    const list = (p.frame.components || []).slice();
    const i = list.findIndex((c) => c.key === compKey);
    if (i === -1) list.push({ key: compKey, material_id: null, enabled: true, [prop]: value });
    else list[i] = { ...list[i], [prop]: value };
    return { ...p, frame: { ...p.frame, components: list } };
  });

  const exportExcel = () => window.open(projectsApi.exportUrl(id), "_blank");

  const newManualLine = () => ({ id: crypto.randomUUID(), label: "", quantity: 0, unit: "m²", material_id: null, unit_price: 0, group: "Diğer", enabled: true });
  const addManual = () => update((p) => ({ ...p, frame: { ...p.frame, manual_lines: [...(p.frame.manual_lines || []), newManualLine()] } }));
  const addManualPreset = (label, group, unit = "gün") => update((p) => ({ ...p, frame: { ...p.frame, manual_lines: [...(p.frame.manual_lines || []), { id: crypto.randomUUID(), label, quantity: 0, unit, material_id: null, unit_price: 0, group, enabled: true }] } }));
  const setManual = (mid, prop, value) => update((p) => {
    const arr = (p.frame.manual_lines || []).slice();
    const i = arr.findIndex((m) => m.id === mid);
    if (i !== -1) arr[i] = { ...arr[i], [prop]: value };
    return { ...p, frame: { ...p.frame, manual_lines: arr } };
  });
  const removeManual = (mid) => update((p) => ({ ...p, frame: { ...p.frame, manual_lines: (p.frame.manual_lines || []).filter((m) => m.id !== mid) } }));
  const copyComponent = (c) => update((p) => {
    const line = { id: crypto.randomUUID(), label: `${c.label} (kopya)`, quantity: c.quantity, unit: c.unit, material_id: c.material_id || null, unit_price: c.unitPrice || 0, group: "Malzeme", enabled: true };
    return { ...p, frame: { ...p.frame, manual_lines: [...(p.frame.manual_lines || []), line] } };
  });

  const others = Object.keys(WOOD_SYSTEM_TYPES).filter((t) => t !== st);
  const baseName = () => project.name.replace(/\s*\((CLT|Yığma|Karkas)\)\s*$/, "");
  const ensureGroup = async () => {
    let vg = project.variant_group;
    if (!vg) {
      vg = crypto.randomUUID();
      const next = { ...project, variant_group: vg };
      setProject(next);
      await persist(next);
    } else {
      await persist(project);
    }
    return vg;
  };
  const makeVariant = (target, vg) => projectsApi.create({
    name: `${baseName()} (${WOOD_SYSTEM_TYPES[target]})`,
    category: "wooden_house", system_type: target,
    prepared_by: project.prepared_by, project_date: project.project_date,
    price_mode: "live", line_items: [], frame: project.frame, variant_group: vg,
  });
  const saveAs = async (target) => {
    clearTimeout(saveTimer.current);
    const vg = await ensureGroup();
    try {
      const created = await makeVariant(target, vg);
      toast.success(`${WOOD_SYSTEM_TYPES[target]} sistemi olarak kaydedildi`, {
        action: { label: "Aç", onClick: () => nav(`/projects/${created.id}`) },
      });
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  const saveAllOthers = async () => {
    clearTimeout(saveTimer.current);
    const vg = await ensureGroup();
    for (const t of others) { await makeVariant(t, vg).catch(() => {}); }
    toast.success("Diğer sistemler bu metrajla oluşturuldu");
  };

  const num = (val, onChange, testid, ph = "") => (
    <input type="number" step="0.01" value={val ?? ""} data-testid={testid} placeholder={ph}
      onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
  );

  const areaField = (key, label, withSlope = false) => {
    const a = k[key] || DEFAULT_AREA;
    const eff = (parseFloat(a.area_override) || 0) > 0 ? parseFloat(a.area_override) : (parseFloat(a.width) || 0) * (parseFloat(a.length) || 0);
    return (
      <div className="rounded-md border border-slate-200 p-3">
        <div className="text-xs font-medium text-slate-500 mb-2">{label}</div>
        <div className="grid grid-cols-3 gap-2">
          {num(a.width, (v) => setArea(key, "width", v), `${key}-width`, "En (m)")}
          {num(a.length, (v) => setArea(key, "length", v), `${key}-length`, "Boy (m)")}
          {num(a.area_override, (v) => setArea(key, "area_override", v), `${key}-area`, "m²")}
        </div>
        {withSlope && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-slate-500 whitespace-nowrap">Çatı eğimi (%)</span>
            {num(k.roof_slope_pct, (v) => setFrame("roof_slope_pct", v), "roof-slope")}
          </div>
        )}
        <div className="text-right text-sm font-semibold text-amber-800 mt-1 tabular-nums" data-testid={`area-${key}`}>= {fmt(eff)} m²</div>
      </div>
    );
  };

  const wallTable = (field, title) => (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-900">{title}</h3>
        <button onClick={() => addItem(field, newWall)} data-testid={`add-${field}`}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
          <Plus className="w-3.5 h-3.5" /> Ekle
        </button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
          <th className="text-left pb-1">Ad</th><th className="text-right pb-1 w-20">En (m)</th>
          <th className="text-right pb-1 w-20">Boy (m)</th><th className="text-right pb-1 w-20">m²</th><th className="w-6"></th>
        </tr></thead>
        <tbody>
          {k[field].length === 0 ? (
            <tr><td colSpan={5} className="py-3 text-center text-slate-400 text-xs">Kayıt yok.</td></tr>
          ) : k[field].map((w, idx) => (
            <tr key={w.id} className="border-t border-slate-100">
              <td className="py-1 pr-1">
                <input value={w.name} placeholder={`Duvar ${idx + 1}`} data-testid={`${field}-${idx}-name`}
                  onChange={(e) => setItem(field, idx, "name", e.target.value)}
                  className="w-full rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-amber-600" />
              </td>
              <td className="py-1 px-0.5">{num(w.width, (v) => setItem(field, idx, "width", v), `${field}-${idx}-width`)}</td>
              <td className="py-1 px-0.5">{num(w.height, (v) => setItem(field, idx, "height", v), `${field}-${idx}-height`)}</td>
              <td className="py-1 px-0.5 text-right tabular-nums text-slate-700">{fmt((parseFloat(w.width) || 0) * (parseFloat(w.height) || 0))}</td>
              <td className="py-1 text-center">
                <button onClick={() => removeItem(field, idx)} data-testid={`${field}-${idx}-delete`} className="p-1 rounded text-red-400 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const openingTable = (field, title) => (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-900">{title}</h3>
        <button onClick={() => addItem(field, newOpening)} data-testid={`add-${field}`}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
          <Plus className="w-3.5 h-3.5" /> Ekle
        </button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
          <th className="text-right pb-1 w-20">En (m)</th><th className="text-right pb-1 w-20">Boy (m)</th>
          <th className="text-right pb-1 w-16">Adet</th><th className="text-right pb-1 w-20">m²</th><th className="w-6"></th>
        </tr></thead>
        <tbody>
          {k[field].length === 0 ? (
            <tr><td colSpan={5} className="py-3 text-center text-slate-400 text-xs">Kayıt yok.</td></tr>
          ) : k[field].map((o, idx) => (
            <tr key={o.id} className="border-t border-slate-100">
              <td className="py-1 px-0.5">{num(o.width, (v) => setItem(field, idx, "width", v), `${field}-${idx}-width`)}</td>
              <td className="py-1 px-0.5">{num(o.height, (v) => setItem(field, idx, "height", v), `${field}-${idx}-height`)}</td>
              <td className="py-1 px-0.5">{num(o.count, (v) => setItem(field, idx, "count", v), `${field}-${idx}-count`)}</td>
              <td className="py-1 px-0.5 text-right tabular-nums text-slate-700">{fmt((parseFloat(o.width) || 0) * (parseFloat(o.height) || 0) * (parseFloat(o.count) || 0))}</td>
              <td className="py-1 text-center">
                <button onClick={() => removeItem(field, idx)} data-testid={`${field}-${idx}-delete`} className="p-1 rounded text-red-400 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const selByKey = Object.fromEntries((k.components || []).map((c) => [c.key, c]));

  return (
    <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => nav("/")} data-testid="back-btn" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Tüm projeler
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="save-status">
            {saving ? (<><Save className="w-3.5 h-3.5 animate-pulse" /> Kaydediliyor…</>)
              : savedAt ? (<><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Kaydedildi</>) : null}
          </span>
          <button onClick={exportExcel} data-testid="export-excel-btn"
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
            <Download className="w-4 h-4" /> Excel'e Aktar
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Proje adı</label>
            <input data-testid="header-name-input" value={project.name} onChange={(e) => setMeta("name", e.target.value)}
              className="mt-1 w-full font-heading text-2xl font-extrabold text-slate-900 bg-transparent border-b border-transparent focus:border-amber-500 outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ahşap ev sistemi</label>
            <Select value={st} onValueChange={changeSystem}>
              <SelectTrigger data-testid="system-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(WOOD_SYSTEM_TYPES).map(([kk, v]) => (<SelectItem key={kk} value={kk}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hazırlayan</label>
            <input data-testid="header-preparedby-input" value={project.prepared_by || ""} onChange={(e) => setMeta("prepared_by", e.target.value)} placeholder="İsim"
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1" />
          </div>
        </div>
      </div>

      {/* Save this metraj as other systems */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 flex flex-wrap items-center gap-3" data-testid="save-as-bar">
        <span className="text-sm font-medium text-slate-700">Aynı metrajı farklı sistemde de kaydet:</span>
        {others.map((t) => (
          <button key={t} onClick={() => saveAs(t)} data-testid={`save-as-${t}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors">
            <Copy className="w-4 h-4" /> {WOOD_SYSTEM_TYPES[t]} olarak kaydet
          </button>
        ))}
        <button onClick={saveAllOthers} data-testid="save-as-all"
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
          Diğer sistemlerin hepsini oluştur
        </button>
      </div>

      {/* Metraj */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 space-y-3">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Home className="w-5 h-5 text-amber-600" /> Metraj Girdileri</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {areaField("footprint", "Ev oturum alanı")}
          {areaField("mezz1", "Ara kat 1")}
          {areaField("mezz2", "Ara kat 2")}
          {areaField("roof_projection", "Çatı izdüşümü", true)}
          {areaField("veranda", "Veranda")}
          {areaField("balcony", "Balkon")}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
          <div><label className="text-xs font-medium text-slate-500">Çatı yapısal katsayı (m³/m²)</label>{num(k.roof_structural_coeff, (v) => setFrame("roof_structural_coeff", v), "roof-coeff")}</div>
          <div><label className="text-xs font-medium text-slate-500">Zemin yapısal katsayı (m³/m²)</label>{num(k.floor_structural_coeff, (v) => setFrame("floor_structural_coeff", v), "floor-coeff")}</div>
          {(isYigma || isClt) && (
            <>
              <div><label className="text-xs font-medium text-slate-500">İç duvar kalınlığı (m)</label>{num(k.interior_wall_thickness, (v) => setFrame("interior_wall_thickness", v), "int-thickness")}</div>
              <div><label className="text-xs font-medium text-slate-500">Dış duvar kalınlığı (m)</label>{num(k.exterior_wall_thickness, (v) => setFrame("exterior_wall_thickness", v), "ext-thickness")}</div>
            </>
          )}
          {isClt && (
            <>
              <div><label className="text-xs font-medium text-slate-500">Çatı CLT panel kalınlığı (m)</label>{num(k.roof_clt_thickness, (v) => setFrame("roof_clt_thickness", v), "roof-clt-thickness")}</div>
              <div><label className="text-xs font-medium text-slate-500">Döşeme CLT panel kalınlığı (m)</label>{num(k.floor_clt_thickness, (v) => setFrame("floor_clt_thickness", v), "floor-clt-thickness")}</div>
            </>
          )}
          {!isYigma && !isClt && (
            <div><label className="text-xs font-medium text-slate-500">Duvar katsayısı</label>{num(k.wall_coeff, (v) => setFrame("wall_coeff", v), "wall-coeff")}</div>
          )}
        </div>
      </div>

      {/* Walls */}
      <div className="grid gap-5 lg:grid-cols-2">
        {wallTable("interior_walls", "İç Duvarlar (en × boy)")}
        {wallTable("exterior_walls", "Dış Duvarlar (en × boy)")}
      </div>

      {/* Openings */}
      <div className="grid gap-5 lg:grid-cols-3">
        {openingTable("windows", "Pencereler")}
        {openingTable("exterior_doors", "Dış Kapılar")}
        {openingTable("interior_doors", "İç Kapılar")}
      </div>

      {/* Input totals */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Girdi Toplamları</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            ["Oturum alanı", areas.footprint, "m²"],
            ["Ara kat toplam", areas.mezz, "m²"],
            ["Toplam döşeme", areas.total, "m²"],
            ["Çatı alanı", areas.roof, "m²"],
            ["İç duvar toplam", areas.iw, "m²"],
            ["Dış duvar toplam", areas.ew, "m²"],
            ["Pencere toplam", areas.win, "m²"],
            ["Dış kapı toplam", areas.extd, "m²"],
            ["İç kapı toplam", areas.intd, "m²"],
            ["Veranda", areas.veranda, "m²"],
            ["Balkon", areas.balcony, "m²"],
          ].map(([label, value, unit]) => (
            <div key={label} className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs text-slate-500">{label}</div>
              <div className="text-lg font-extrabold tabular-nums text-slate-900" data-testid={`total-${label}`}>
                {fmt(value)} <span className="text-xs font-medium text-slate-400">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Components + material selection */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-heading text-lg font-bold text-slate-900">Hesaplanan Bileşenler & Malzeme Seçimi</h2>
          <p className="text-xs text-slate-400 mt-1">Miktarlar metrajdan otomatik hesaplanır. Malzeme seçince birim fiyat kütüphaneden gelir; istemediğiniz kalemi kapatabilirsiniz.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-100">
                {["Dahil", "Bileşen", "Miktar", "Birim", "Malzeme (kütüphane)", "Birim Fiyat", "Tutar", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i === 2 || (i >= 5 && i <= 6) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comps.map((c) => {
                const sel = selByKey[c.key] || {};
                const enabled = sel.enabled !== false;
                const wantUnit = UNIT_MAP[c.unit];
                const opts = materials.filter((m) => m.calc_unit === wantUnit);
                const list = opts.length ? opts : materials;
                return (
                  <tr key={c.key} className={`border-b border-slate-100 ${enabled ? "" : "opacity-40"}`} data-testid={`comp-${c.key}`}>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={enabled} data-testid={`comp-${c.key}-enabled`}
                        onChange={(e) => setComp(c.key, "enabled", e.target.checked)} className="w-4 h-4 accent-amber-600" />
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-slate-900">{c.label}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums font-semibold text-amber-900" data-testid={`comp-${c.key}-qty`}>{fmt(c.quantity)}</td>
                    <td className="px-3 py-2 text-sm text-slate-500">{c.unit}</td>
                    <td className="px-3 py-2">
                      <Select value={sel.material_id || ""} onValueChange={(v) => setComp(c.key, "material_id", v)}>
                        <SelectTrigger data-testid={`comp-${c.key}-material`} className="h-9 text-sm"><SelectValue placeholder="Malzeme seç" /></SelectTrigger>
                        <SelectContent>
                          {list.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-1 w-28">
                      {sel.material_id ? (
                        <div className="text-sm text-right tabular-nums text-slate-700 py-1.5">{fmtMoney(c.unitPrice)}</div>
                      ) : (
                        <input type="number" step="0.01" value={sel.unit_price ?? 0} data-testid={`comp-${c.key}-price`}
                          onChange={(e) => setComp(c.key, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                          className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums font-bold text-slate-900" data-testid={`comp-${c.key}-total`}>{fmtMoney(c.lineTotal)}</td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => copyComponent(c)} data-testid={`comp-${c.key}-copy`} title="Maliyet satırı olarak kopyala"
                        className="p-1.5 rounded text-slate-400 hover:bg-amber-50 hover:text-amber-700"><Copy className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}

              {manual.length > 0 && (() => {
                const byGroup = {};
                manual.forEach((m) => { (byGroup[m.group] = byGroup[m.group] || []).push(m); });
                const order = [...MANUAL_GROUPS.filter((g) => byGroup[g]), ...Object.keys(byGroup).filter((g) => !MANUAL_GROUPS.includes(g))];
                return order.map((g) => {
                  const lines = byGroup[g];
                  const sub = lines.reduce((s, x) => s + x.lineTotal, 0);
                  return (
                    <Fragment key={g}>
                      <tr className="bg-slate-100">
                        <td colSpan={6} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-600" data-testid={`manual-group-${g}`}>» {g}</td>
                        <td className="px-3 py-1.5 text-right text-xs font-bold tabular-nums text-slate-700" data-testid={`manual-group-${g}-subtotal`}>{fmtMoney(sub)}</td>
                        <td></td>
                      </tr>
                      {lines.map((m) => (
                <tr key={m.id} className={`border-b border-slate-100 ${m.enabled ? "" : "opacity-40"}`} data-testid={`manual-${m.id}`}>
                  <td className="px-3 py-2 text-center align-top">
                    <input type="checkbox" checked={m.enabled} data-testid={`manual-${m.id}-enabled`}
                      onChange={(e) => setManual(m.id, "enabled", e.target.checked)} className="w-4 h-4 accent-amber-600" />
                  </td>
                  <td className="px-3 py-1">
                    <input value={m.label} placeholder="Kalem açıklaması" data-testid={`manual-${m.id}-label`}
                      onChange={(e) => setManual(m.id, "label", e.target.value)}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber-600" />
                    <select value={m.group} data-testid={`manual-${m.id}-group`}
                      onChange={(e) => setManual(m.id, "group", e.target.value)}
                      className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 bg-white outline-none focus:ring-2 focus:ring-amber-600">
                      {MANUAL_GROUPS.map((gg) => <option key={gg} value={gg}>{gg}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1 w-24 align-top">
                    <input type="number" step="0.01" value={m.quantity} data-testid={`manual-${m.id}-qty`}
                      onChange={(e) => setManual(m.id, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="px-3 py-1 w-16 align-top">
                    <input value={m.unit} data-testid={`manual-${m.id}-unit`}
                      onChange={(e) => setManual(m.id, "unit", e.target.value)}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="px-3 py-1 align-top">
                    <Select value={m.material_id || "none"} onValueChange={(v) => setManual(m.id, "material_id", v === "none" ? null : v)}>
                      <SelectTrigger data-testid={`manual-${m.id}-material`} className="h-9 text-sm"><SelectValue placeholder="Malzeme (opsiyonel)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— (elle fiyat)</SelectItem>
                        {materials.map((mat) => (<SelectItem key={mat.id} value={mat.id}>{mat.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-1 w-28 align-top">
                    {m.material_id ? (
                      <div className="text-sm text-right tabular-nums text-slate-500 py-1.5" data-testid={`manual-${m.id}-price`}>{fmtMoney(m.unitPrice)}</div>
                    ) : (
                      <input type="number" step="0.01" value={m.unitPrice} data-testid={`manual-${m.id}-price`}
                        onChange={(e) => setManual(m.id, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums font-bold text-slate-900 align-top" data-testid={`manual-${m.id}-total`}>{fmtMoney(m.lineTotal)}</td>
                  <td className="px-2 py-2 text-center align-top">
                    <button onClick={() => removeManual(m.id)} data-testid={`manual-${m.id}-delete`} className="p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
                      ))}
                    </Fragment>
                  );
                });
              })()}

              <tr>
                <td colSpan={8} className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={addManual} data-testid="add-manual-line"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Manuel maliyet satırı ekle
                    </button>
                    <button onClick={() => addManualPreset("", "Malzeme", "adet")} data-testid="add-malzeme"
                      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100">
                      <Plus className="w-4 h-4" /> Manuel malzeme satırı ekle
                    </button>
                    <button onClick={() => addManualPreset("Konaklama", "İşçilik", "gece")} data-testid="add-konaklama"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Konaklama (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Yeme-içme", "İşçilik", "kişi-gün")} data-testid="add-yemeicme"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Yeme-içme (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Yevmiye", "İşçilik", "yevmiye")} data-testid="add-yevmiye"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Yevmiye (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Vinç", "İşçilik", "gün")} data-testid="add-vinc"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Vinç (İşçilik)
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200">
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">Malzeme Maliyeti</td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="material-total">{fmtMoney(materialTotal)}</td>
              </tr>
              {otherGroups.map((g) => (
                <tr key={g}>
                  <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">{g === "İşçilik" ? "İşçilik Maliyeti" : `${g} Maliyeti`}</td>
                  <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid={`group-total-${g}`}>{fmtMoney(groupSums[g])}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200">
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">Ara Toplam</td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="subtotal">{fmtMoney(grand)}</td>
              </tr>
              <tr>
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">
                  <span className="inline-flex items-center gap-2 justify-end">
                    Genel Gider
                    <input type="number" step="0.1" value={k.overhead_pct ?? 0} data-testid="overhead-pct"
                      onChange={(e) => setFrame("overhead_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-20 rounded border border-slate-200 px-2 py-1 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                    %
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="overhead-amount">{fmtMoney(overhead)}</td>
              </tr>
              <tr className="bg-amber-50 border-t-2 border-amber-200">
                <td colSpan={7} className="px-3 py-3 text-right text-sm font-bold uppercase tracking-wider text-slate-700">Toplam Maliyet</td>
                <td className="px-3 py-3 text-right text-lg font-extrabold tabular-nums text-amber-900 whitespace-nowrap" data-testid="grand-total">{fmtMoney(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
